import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Search } from "lucide-react";
import { api, formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { EmptyState, RowSkeleton } from "../components/Skeletons";

export default function POHistory() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [vendorId, setVendorId] = useState("all");
  const [deptId, setDeptId] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detail, setDetail] = useState(null);
  const [vendorMap, setVendorMap] = useState({});
  const [deptMap, setDeptMap] = useState({});

  const load = async () => {
    setLoading(true);
    const params = {
      page, page_size: pageSize,
      vendor_id: vendorId === "all" ? undefined : vendorId,
      department_id: deptId === "all" ? undefined : deptId,
      q: q || undefined,
    };
    const [r, v, d] = await Promise.all([
      api.get("/purchase-orders", { params }),
      vendors.length ? Promise.resolve({ data: vendors }) : api.get("/vendors"),
      departments.length ? Promise.resolve({ data: departments }) : api.get("/departments"),
    ]);
    setData(r.data);
    if (!vendors.length) {
      setVendors(v.data);
      const vm = {}; v.data.forEach((x) => (vm[x.id] = x));
      setVendorMap(vm);
    }
    if (!departments.length) {
      setDepartments(d.data);
      const dm = {}; d.data.forEach((x) => (dm[x.id] = x));
      setDeptMap(dm);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize, vendorId, deptId]);

  const downloadPdf = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${po.po_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      if (vendorId !== "all") params.set("vendor_id", vendorId);
      if (deptId !== "all") params.set("department_id", deptId);
      if (q) params.set("q", q);
      const res = await api.get(`/purchase-orders/export/xlsx?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `purchase_orders.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="po-history-title">
            PO history
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Search, filter, download.</p>
        </div>
        <Button onClick={exportXlsx} variant="outline" className="gap-2" data-testid="export-xlsx">
          <FileSpreadsheet size={14} /> Export Excel
        </Button>
      </div>

      <div className="k-glass rounded-2xl p-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="PO# or contact..." className="pl-9" data-testid="po-history-search" />
        </div>
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger className="w-44" data-testid="po-history-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={deptId} onValueChange={setDeptId}>
          <SelectTrigger className="w-44" data-testid="po-history-dept"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="w-28" data-testid="po-history-pagesize"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="k-glass rounded-2xl overflow-hidden">
        {loading ? (
          <div>{Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)}</div>
        ) : data.items.length === 0 ? (
          <div className="p-2"><EmptyState title="No POs found" description="Try changing filters." /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Dispatch</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={`po-row-${p.po_number}`}
                  className="cursor-pointer hover:bg-foreground/5 transition"
                  onClick={() => setDetail(p)}
                >
                  <TableCell className="font-semibold text-[#534AB7]">{p.po_number}</TableCell>
                  <TableCell>{p.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>{p.vendor_name || "—"}</TableCell>
                  <TableCell>{p.department_name || "—"}</TableCell>
                  <TableCell>{p.lines?.length || 0}</TableCell>
                  <TableCell>{p.last_dispatch_date || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">₹{(p.total_amount || 0).toLocaleString()}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(p)} data-testid={`view-${p.po_number}`} className="gap-1">
                        <Eye size={14} /> View
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(p)} data-testid={`download-${p.po_number}`} className="gap-1">
                        <Download size={14} /> PDF
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          Page {page} of {totalPages} · {data.total} POs
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">Prev</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next">Next</Button>
        </div>
      </div>

      {/* PO detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDetail(null)}
                className="gap-1 -ml-2"
                data-testid="po-detail-back"
              >
                <ArrowLeft size={14} /> Back
              </Button>
              <DialogTitle className="flex-1">
                <span className="text-[#534AB7]">{detail?.po_number}</span>
                <span className="text-muted-foreground text-sm font-normal ml-3">{detail?.created_at?.slice(0, 10)}</span>
              </DialogTitle>
            </div>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendor</div>
                  <div className="font-medium">{vendorMap[detail.vendor_id]?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Department</div>
                  <div className="font-medium">{deptMap[detail.department_id]?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Contact</div>
                  <div className="font-medium">{detail.contact_person || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Transport</div>
                  <div className="font-medium">{detail.transport_mode || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last dispatch</div>
                  <div className="font-medium">{detail.last_dispatch_date || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Delivery</div>
                  <div className="font-medium">{detail.delivery_date || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Remarks</div>
                  <div className="font-medium">{detail.remarks || "—"}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Line items</div>
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.lines || []).map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="font-medium">{l.item_name}</TableCell>
                          <TableCell>{l.size || "—"}</TableCell>
                          <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">₹{Number(l.rate).toLocaleString()}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">₹{Number(l.amount).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="text-sm text-muted-foreground">Total ({detail.lines?.length || 0} line items)</div>
                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>
                  ₹{(detail.total_amount || 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex sm:justify-between">
            <Button variant="outline" onClick={() => setDetail(null)} className="gap-1" data-testid="po-detail-close">
              <ArrowLeft size={14} /> Back to list
            </Button>
            {detail && (
              <Button onClick={() => downloadPdf(detail)} className="bg-[#534AB7] hover:bg-[#463E9F] gap-1" data-testid="po-detail-download">
                <Download size={14} /> Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
